import { useState, useEffect } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Alert, Badge, Text } from '@backstage/ui';
import { InfraProgress } from '../ui/InfraPrimitives';
import { ExtendTtlButton } from './ExtendTtlButton';
import { shouldWarnWeekendExpiry } from './ttlCalendar';
import styles from './ttlWarningCard.module.css';

export const TTLWarningCard = () => {
  const { entity } = useEntity();
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [percentRemaining, setPercentRemaining] = useState<number>(100);

  const expirationDate =
    entity.metadata.annotations?.['infrastructure.backstage.io/expiration-date'];
  const ttlHours =
    entity.metadata.annotations?.['infrastructure.backstage.io/ttl-hours'];
  const createdAt =
    entity.metadata.annotations?.['infrastructure.backstage.io/created-at'];
  const showCard =
    !!expirationDate && expirationDate !== 'never' && ttlHours !== '0';

  useEffect(() => {
    if (!showCard || !expirationDate) {
      return undefined;
    }

    const updateTimeRemaining = () => {
      const now = new Date();
      const expiration = new Date(expirationDate);
      const created = createdAt ? new Date(createdAt) : new Date();

      const totalTime = expiration.getTime() - created.getTime();
      const remaining = expiration.getTime() - now.getTime();

      setTimeRemaining(remaining);
      setPercentRemaining(
        Math.max(0, Math.min(100, (remaining / totalTime) * 100)),
      );
    };

    updateTimeRemaining();

    const interval = setInterval(updateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [showCard, expirationDate, createdAt]);

  if (!showCard) {
    return null;
  }

  const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor(
    (timeRemaining % (1000 * 60 * 60)) / (1000 * 60),
  );
  const isExpired = timeRemaining <= 0;
  const isCritical = hoursRemaining < 4 && !isExpired;
  const isWarning = hoursRemaining < 24 && hoursRemaining >= 4;
  const weekendRisk =
    !isExpired &&
    !!expirationDate &&
    shouldWarnWeekendExpiry(new Date(expirationDate));

  const alertStatus = isExpired
    ? 'danger'
    : isCritical
      ? 'danger'
      : isWarning
        ? 'warning'
        : 'info';

  const progressTone = isExpired
    ? 'danger'
    : isCritical
      ? 'danger'
      : isWarning
        ? 'warning'
        : 'default';

  const formatTimeRemaining = () => {
    if (isExpired) {
      return 'EXPIRED - Scheduled for auto-destroy';
    }
    if (hoursRemaining < 1) {
      return `${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`;
    }
    if (hoursRemaining < 24) {
      return `${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''} ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`;
    }
    const days = Math.floor(hoursRemaining / 24);
    const hours = hoursRemaining % 24;
    return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
  };

  return (
    <InfoCard
      title="⏰ Auto-Destroy Schedule"
      className={styles.card}
      deepLink={{
        title: 'View Cleanup Workflow',
        link: 'https://github.com/Redis-ProfessionalService/ps-backstage-infra/actions/workflows/auto-cleanup.yml',
      }}
    >
      <Alert
        status={alertStatus}
        icon
        title={
          isExpired
            ? '🗑️ Infrastructure Expired'
            : '⏰ Time to Live (TTL) Active'
        }
        description={
          <>
            <Text variant="body-small" as="p">
              {isExpired ? (
                <>
                  This infrastructure has expired and is scheduled for automatic
                  destruction. The cleanup workflow runs every hour.
                </>
              ) : (
                <>
                  This infrastructure will be automatically destroyed in{' '}
                  <strong>{formatTimeRemaining()}</strong>
                </>
              )}
            </Text>

            {!isExpired && (
              <InfraProgress value={percentRemaining} tone={progressTone} />
            )}

            <div className={styles.expirationInfo}>
              <Text variant="body-x-small" as="p">
                <strong>Created:</strong>{' '}
                {createdAt
                  ? new Date(createdAt).toLocaleString()
                  : 'Unknown'}
              </Text>
              <Text variant="body-x-small" as="p">
                <strong>Expires:</strong>{' '}
                {new Date(expirationDate).toLocaleString()}
              </Text>
              <Text variant="body-x-small" as="p">
                <strong>TTL:</strong> {ttlHours} hours
              </Text>
            </div>

            {(isCritical || isWarning || weekendRisk) && !isExpired && (
              <div className={styles.tagRow}>
                {isCritical || isWarning ? (
                  <Badge size="small">
                    {isCritical
                      ? '⚠️ Critical: Less than 4 hours remaining'
                      : '⏰ Warning: Less than 24 hours remaining'}
                  </Badge>
                ) : null}
                {weekendRisk ? (
                  <Badge size="small">
                    {`📅 Expires over the weekend — extend now if you need it Monday`}
                  </Badge>
                ) : null}
              </div>
            )}

            <div className={styles.extendRow}>
              <ExtendTtlButton />
            </div>
          </>
        }
      />
    </InfoCard>
  );
};
